/**
 * Done. / ChiroCPD — CapIndicator Primitive
 *
 * Three states: healthy / warning (≥80%) / exhausted (100%)
 * Variant: badge (dashboard) | inline (form sidebar)
 * Covers both hours and AI generation caps.
 */

import { cx, capHealth } from '@/lib/utils';
import type { CapUnit, CapVariant } from '@/lib/types';

interface CapIndicatorProps {
  current: number;
  max: number;
  unit: CapUnit;
  variant?: CapVariant;
  className?: string;
}

const LABEL: Record<CapUnit, (current: number, max: number) => string> = {
  hours: (c, m) => `${String(Math.max(0, m - c))} hrs remaining this year`,
  generations: (c, m) => `${String(Math.max(0, m - c))} of ${String(m)} AI drafts remaining`,
};

export function CapIndicator({
  current,
  max,
  unit,
  variant = 'badge',
  className,
}: CapIndicatorProps): React.ReactElement {
  const health = capHealth(current, max);
  const label = LABEL[unit](current, max);

  const colours = {
    healthy:   { text: 'text-[var(--color-done-700)]', bg: 'bg-[var(--color-done-50)]',    border: 'border-[var(--color-done-200)]' },
    warning:   { text: 'text-amber-700',                bg: 'bg-[var(--color-warning-100)]', border: 'border-[var(--color-cpd-amber)]' },
    exhausted: { text: 'text-red-700',                  bg: 'bg-[var(--color-error-100)]',   border: 'border-[var(--color-error-500)]' },
  }[health];

  if (variant === 'inline') {
    return (
      <p
        className={cx('text-xs font-medium', colours.text, className)}
        aria-label={label}
      >
        {label}
      </p>
    );
  }

  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border',
        colours.text,
        colours.bg,
        colours.border,
        className,
      )}
      aria-label={label}
    >
      {label}
    </span>
  );
}
